/*
 * Vendored from CopilotKit (https://github.com/CopilotKit/CopilotKit)
 * packages/channels-slack/src/message-stream.ts @ 66fef88b249e9cac2d76a238678c19d3ce6b2913.
 * Copyright (c) Atai Barkai — MIT License.
 * Adapted for @skaile/workspaces/channels: platform-neutral naming and
 * parametric limits; Slack SDK imports removed. See NOTICE at package root.
 */

/**
 * Options for {@link SerializedMessageStream}.
 */
export interface SerializedMessageStreamOptions {
  /** Writes `content` to the platform message (e.g. an edit/update call). */
  apply: (content: string) => Promise<void>;
  /** Minimum gap between consecutive applies, in ms. `flush()` bypasses it. */
  throttleMs: number;
  /**
   * Sink for a failed `apply`; defaults to `console.error`. The failure is
   * swallowed after reporting: a failed FINAL apply leaves the platform
   * message stale, and nothing retries it unless newer content arrives.
   */
  onApplyError?: (err: unknown) => void;
  /** Clock override for tests; defaults to `Date.now`. */
  now?: () => number;
}

/**
 * Per-message streaming text state machine.
 *
 * Wraps a single posted platform message and lets callers feed it the growing
 * text buffer of a streaming reply. Edits are throttled (chat platforms
 * rate-limit message updates) and *serialised* through a per-message promise
 * queue so concurrent updates can't race — a hazard the upstream project hit,
 * where an end-triggered flush of "ALPHA" could be overtaken by an in-flight
 * flush of "AL" and the final state read "AL".
 *
 * Guarantees: at most one `apply` in flight; applies happen in enqueue order;
 * a newer `enqueue` supersedes older un-applied content (no stale flush); no
 * `apply` more often than `throttleMs` except on `flush()`.
 */
export class SerializedMessageStream {
  private buffer = "";
  private applied = "";
  private queue: Promise<void> = Promise.resolve();
  private lastApplyAt = 0;
  private flushTimer: ReturnType<typeof setTimeout> | undefined;
  private readonly throttleMs: number;
  private readonly apply: (content: string) => Promise<void>;
  private readonly onApplyError: (err: unknown) => void;
  private readonly now: () => number;

  constructor(opts: SerializedMessageStreamOptions) {
    this.apply = opts.apply;
    this.throttleMs = opts.throttleMs;
    this.onApplyError =
      opts.onApplyError ?? ((err) => console.error("[channels:message-stream] apply failed:", err));
    this.now = opts.now ?? Date.now;
  }

  /** Replace the in-flight buffer (callers pass the accumulated text). */
  enqueue(content: string): void {
    if (content === this.buffer) return;
    this.buffer = content;
    this.scheduleFlush();
  }

  /**
   * Mark the stream done. Cancels any pending throttled flush, enqueues a
   * final flush, and resolves once the entire queue (including the final
   * flush and anything previously in flight) has drained.
   *
   * After this resolves, the platform message reflects the final buffer state.
   */
  async flush(): Promise<void> {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = undefined;
    }
    this.enqueueFlush();
    await this.queue;
  }

  private scheduleFlush(): void {
    if (this.flushTimer) return;
    const elapsed = this.now() - this.lastApplyAt;
    const delay = Math.max(0, this.throttleMs - elapsed);
    this.flushTimer = setTimeout(() => {
      this.flushTimer = undefined;
      this.enqueueFlush();
    }, delay);
  }

  private enqueueFlush(): void {
    this.queue = this.queue.then(() => this.flushNow());
  }

  private async flushNow(): Promise<void> {
    if (this.buffer === this.applied) return;
    const content = this.buffer;
    this.applied = content;
    try {
      await this.apply(content);
    } catch (err) {
      // A single failed apply shouldn't sink the stream — report and swallow;
      // future enqueues retry with the latest buffer. (Platform SDKs typically
      // retry rate-limit responses themselves before an error surfaces here.)
      this.onApplyError(err);
    } finally {
      // Set lastApplyAt *after* the apply returns so the throttle measures
      // wall-clock time between completions, not starts.
      this.lastApplyAt = this.now();
    }
  }
}
